import { useObservableState } from 'observable-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Checkbox from '../../../components/form/elements/checkbox';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import Select, {
  emptySelectOption,
  Option,
} from '../../../components/form/elements/select';
import Text from '../../../components/form/elements/text';
import Address from '../../../components/form/elements/address';
import TextArea from '../../../components/form/elements/text-area';
import Warehouse from '../../../interfaces/warehouse';
import {
  WAREHOUSE_WAREHOUSES_INDEX,
  WAREHOUSE_WAREHOUSES_VIEW,
} from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';

const schema = yup.object().shape({
  name: yup.string().max(255).required(),
  type_of_warehouse_id: yup.string().required(),
  address: yup.lazy((addressValue) => {
    if (typeof addressValue === 'object') {
      return yup.object().shape({
        value: yup.string().required(),
      });
    } else {
      return yup.string().max(255).required();
    }
  }),
  coordinates: yup
    .mixed()
    .test('coordinates', 'Field must contain valid coordinates', (value) => {
      if (!value) {
        return true;
      }
      const latLong = value.split(',');
      if (latLong.length !== 2) {
        return false;
      }
      const lat = Number(latLong[0]);
      const long = Number(latLong[1]);
      if (Number.isNaN(lat) || Number.isNaN(long)) {
        return false;
      }
      return Math.abs(lat) <= 90 && Math.abs(long) <= 180;
    }),
});

export default function WarehouseCreate() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId } = useParams<{ farmId: string }>();
  const { warehouseService } = useService();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const [defaultValues] = useState(() => {
    return {
      type_of_warehouse_id: emptySelectOption,
      address: '',
      coordinates: '',
      active: true,
      comment: '',
    };
  });

  const [typesOfWarehouses] = useObservableState<Option[]>(
    () =>
      warehouseService.typesOfWarehouses(farmId).pipe(
        map((values) =>
          values.map((value) => {
            return { value: value.id, label: value.value };
          })
        )
      ),
    null
  );

  useEffect(() => {
    if (typesOfWarehouses !== null) {
      setBodyElements([
        Group([
          Text('name', t('pages.warehouse.warehouses.name')),
          Select('type_of_warehouse_id', {
            options: typesOfWarehouses,
            multi: false,
            label: 'pages.warehouse.warehouses.type',
          }),
          Address('address', t('pages.warehouse.warehouses.address')),
          Text(
            'coordinates',
            t('pages.warehouse.warehouses.coordinates'),
            null,
            {
              sampleText: t(
                'pages.warehouse.warehouses.sample-text.coordinates'
              ),
            }
          ),
          Text(
            'department_pvd_nr',
            t('pages.warehouse.warehouses.department_pvd_nr')
          ),
          Checkbox('active', t('pages.warehouse.warehouses.active')),
          TextArea('comment', t('pages.warehouse.warehouses.comment')),
        ]),
      ]);
      setFooterElements([
        Button('create'),
        Link(route(WAREHOUSE_WAREHOUSES_INDEX, { farmId }), 'cancel'),
      ]);
    }
  }, [typesOfWarehouses, t, setBodyElements, setFooterElements, farmId]);

  const submit = useCallback<SubmitType<Warehouse>>(
    (form: any) => {
      if (typeof form.address === 'object') {
        form.address = form.address?.value;
      }

      return [
        warehouseService.store(farmId, form),
        (response: Warehouse) =>
          history.push(
            route(WAREHOUSE_WAREHOUSES_VIEW, { farmId, id: response.id })
          ),
      ];
    },
    [warehouseService, farmId, history]
  );

  return (
    <Form
      mode="create"
      title={t('pages.warehouse.warehouses.create-warehouse')}
      body={bodyElements}
      footer={footerElements}
      values={defaultValues}
      submit={submit}
      schema={schema}
    />
  );
}
